import React, {useState} from 'react';
import PropTypes from 'prop-types';
import { useSpring, animated, easings } from '@react-spring/web';
import { ReactSVG } from 'react-svg';
import { kidInstrumentAssets, getFontSize } from '../../../../utils/RoundUpUtils';
import sunRays from '../../assets/backgrounds/high_sun_rays.svg';
import triangle_star from '../../assets/triangle_star.svg';
import dash_star from '../../assets/dash_star.svg';
import '../Slides.scss'
import './Skills.scss'

const SkillsUpcomingSlide = ({ data }) => {

  const {
    title,
    first_skill,
    second_skill,
    third_skill,
    instrument
  } = data;


  const [fadeInDone, setFadeInDone] = useState(false);
  const instrumentIcon = instrument && kidInstrumentAssets[instrument];

  const topLineSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 100,
  });

  const titleSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 200,
  });
  
  const subTitleSpring = useSpring({
    from: { opacity: 0, transform: 'translateX(100px)' },
    to: { opacity: 1, transform: 'translateX(0px)' },
    delay: 400,
    config: {
      tension: 180,  // lower tension = more wobble
      friction: 12,  // lower friction = 'less damping'
    },
  });

  const firstSkillSpring = useSpring({
    from: {
      opacity: 0,
      transform: 'rotate(-10deg) translateY(-20px)',
      transformOrigin: 'left bottom',
    },
    to: {
      opacity: 1,
      transform: 'rotate(0deg) translateY(0)',
    },
    config: { tension: 200, friction: 18 },
    delay: 600,
  });

  const secondSkillSpring = useSpring({
    from: {
      opacity: 0,
      transform: 'rotate(10deg) translateY(-20px)',
      transformOrigin: 'right bottom',
    },
    to: {
      opacity: 1,
      transform: 'rotate(0deg) translateY(0)',
    },
    config: { tension: 200, friction: 18 },
    delay: 800,
  });

  const thirdSkillSpring = useSpring({
    from: {
      opacity: 0,
      transform: 'rotate(-10deg) translateY(-20px)',
      transformOrigin: 'left bottom',
    },
    to: {
      opacity: 1,
      transform: 'rotate(0deg) translateY(0)',
    },
    config: { tension: 200, friction: 18 },
    delay: 1000,
  });

  const instrumentSpring = useSpring({
    from: { opacity: 0.5, transform: 'scale(0)' },
    to: { opacity: 1, transform: 'scale(1)' },
    delay: 1200,
    config: {
      tension: 180,
      friction: 12,
    },
  });
  
  const boxSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(100vh)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 800,
  });

  const sunRaysFadeInSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 1000 },
    delay: 1000,
    onRest: () => setFadeInDone(true),
  });

  const sunRaysScaleSpring = useSpring({
    from: { scale: 1 },
    to: { scale: 1.1 },
    loop: fadeInDone ? { reverse: true } : false,
    config: { duration: 1500, easing: easings.easeInOutSine },
  });

  const sunRaysSpring = {
    ...sunRaysFadeInSpring,
    ...sunRaysScaleSpring,
  };

    const appear = useSpring({
      from: { opacity: 0 },
      to: { opacity: 1 }
    })
  
    const spinSparkle = useSpring({
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
      loop: true,
      config: { duration: 3000 },
    });
  
    const pulseSparkle = useSpring({
      from: { transform: 'scale(1)' },
      to: { transform: 'scale(1.3)' },
      loop: { reverse: true },
    });
    
    const twinkleSparkle = useSpring({
      from: { opacity: 0.5 },
      to: { opacity: 1 },
      loop: { reverse: true },
      config: { duration: 800 },
    });
  
    const dashStar1Spring = {
      ...appear,
      ...spinSparkle,
      ...twinkleSparkle,
      delay: 1400
    }

    const dashStar2Spring = {
      ...appear,
      ...spinSparkle,
      ...twinkleSparkle,
      delay: 1400
    }
  
    const triangleStar1Spring = useSpring({
      ...appear,
      ...pulseSparkle,
      delay: 1500,
    });
  
    const triangleStar2Spring = useSpring({
      ...appear,
      delay: 1600,
    });
  
    const triangleStar3Spring = useSpring({
      ...appear,
      ...pulseSparkle,
      delay: 1700,
    });

    const first_skill_font_size = getFontSize(first_skill);
    const second_skill_font_size = getFontSize(second_skill);
    const third_skill_font_size = getFontSize(third_skill);


  return (
    <div className='Slide__skills_upcoming'>
      <animated.h2 className='Slide__recent_skills__topLine' style={topLineSpring}>SOME OF THE</animated.h2>
      <animated.h1 className='Slide__skills_upcoming__title' style={titleSpring}>Skills</animated.h1>
      <animated.h2 className='Slide__skills_upcoming__subTitle' style={subTitleSpring}>{title}</animated.h2>
      <div className='Slide__skills_upcoming__list'>
        {first_skill && (
          <animated.div className='Slide__skills_upcoming__list__item1' style={firstSkillSpring}>
            <p style={{ fontSize: first_skill_font_size }}>{first_skill}</p>
          </animated.div>
        )}
        {second_skill && (
          <animated.div className='Slide__skills_upcoming__list__item2' style={secondSkillSpring}>
            <p style={{ fontSize: second_skill_font_size }}>{second_skill}</p>
          </animated.div>
        )}
        {third_skill && (
          <animated.div className='Slide__skills_upcoming__list__item3' style={thirdSkillSpring}>
            <p style={{ fontSize: third_skill_font_size }}>{third_skill}</p>
          </animated.div>
        )}
      </div>
      {instrument && (
        <animated.div className='Slide__skills_upcoming__instrument' style={instrumentSpring}>
          <ReactSVG src={instrumentIcon} />
        </animated.div>
      )}
      <animated.div className='Slide__skills_upcoming__box' style={boxSpring} />
      <animated.div className='Slide__skills_upcoming__sunRays' style={sunRaysSpring}>
        <ReactSVG src={sunRays} />
      </animated.div>
      <animated.div className='Slide__skills_upcoming__star__triangle1' style={triangleStar1Spring}>
        <ReactSVG src={triangle_star} />
      </animated.div>

      <animated.div className='Slide__skills_upcoming__star__triangle2' style={triangleStar2Spring}>
        <ReactSVG src={triangle_star} />
      </animated.div>
      <animated.div className='Slide__skills_upcoming__star__triangle3' style={triangleStar3Spring}>
        <ReactSVG src={triangle_star} />
      </animated.div>
      <animated.div className='Slide__skills_upcoming__star__dash1' style={dashStar1Spring}>
        <ReactSVG src={dash_star} />
      </animated.div>
      <animated.div className='Slide__skills_upcoming__star__dash2' style={dashStar2Spring}>
        <ReactSVG src={dash_star} />
      </animated.div>
    </div>
  )
}

export default SkillsUpcomingSlide;

const DataProps = {
  first_skill: PropTypes.string.isRequired,
  second_skill: PropTypes.string,
  third_skill: PropTypes.string,
}

SkillsUpcomingSlide.propTypes = {
  data: PropTypes.shape(DataProps).isRequired,
}
